import { useState, type FormEvent } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from '@vht/icons';
import {
  authMode,
  login,
  setMockSession,
  fetchSession,
  sessionQueryKey,
  MOCK_ROLES,
} from '@/lib/auth';
import { PartnerLogin } from '@/features/portal/PartnerLogin';
import { AlfenLogin } from '@/features/portal/AlfenLogin';
import { currentBrand } from '@/lib/brand';
import { LoginBrand, LoginCardLogo } from '@/components/shell/LoginBrand';
import { fetchPortalSession, portalHome, portalSessionQueryKey } from '@/lib/portal';
import './login.css';

interface LoginSearch {
  redirect?: string;
  error?: string;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  beforeLoad: async ({ context }) => {
    // On the names of Alfen there is no internal sign-in (its session lives on our
    // own name), so only the portal session counts.
    if (currentBrand() !== 'alfen') {
      const session = await context.queryClient.ensureQueryData({
        queryKey: sessionQueryKey,
        queryFn: fetchSession,
      });
      // `/` sends them on to where their role lands.
      if (session) throw redirect({ to: '/' });
    }
    // A partner who is signed in already goes straight back to the portal.
    const portal = await context.queryClient.ensureQueryData({
      queryKey: portalSessionQueryKey,
      queryFn: fetchPortalSession,
    });
    if (portal) throw redirect({ to: portalHome(portal) });
  },
  component: LoginPage,
});

function LoginPage() {
  const { redirect: redirectParam } = Route.useSearch();
  if (currentBrand() === 'alfen') return <AlfenLogin redirect={redirectParam} />;
  return <GatewayLogin redirectParam={redirectParam} />;
}

function GatewayLogin({ redirectParam }: { redirectParam?: string }) {
  const { t } = useTranslation();
  const mode = authMode();

  const [mockEmail, setMockEmail] = useState('dev@vht.nl');
  const [mockRole, setMockRole] = useState<string>('admin');

  function resolveTarget(): string {
    if (redirectParam) {
      try {
        const u = new URL(redirectParam, window.location.origin);
        if (u.origin === window.location.origin) return u.pathname + u.search;
      } catch {
        // fall through
      }
    }
    return '/';
  }

  function onMockSubmit(e: FormEvent) {
    e.preventDefault();
    setMockSession({ email: mockEmail, roles: [mockRole] });
    window.location.href = resolveTarget();
  }

  return (
    <div className="login" data-screen-label="Gateway Login">
      <LoginBrand />

      <main className="login-form">
        <div className="login-card">
          <LoginCardLogo />
          <h1>{t('login.title')}</h1>
          <p className="lede">{t('login.lede')}</p>

          {mode === 'entra' ? (
            <div style={{ marginTop: 'var(--space-6)' }}>
              <button
                type="button"
                className="vht-btn vht-btn--primary vht-btn--lg"
                onClick={() => login(resolveTarget())}
              >
                <Icon name="microsoft" size={15} />
                {t('login.sso')}
              </button>
              <p className="sso-note">{t('login.ssoNote')}</p>
            </div>
          ) : (
            <form className="login-mock" onSubmit={onMockSubmit}>
              <span className="login-mock__title">{t('login.mockTitle')}</span>
              <label className="vht-field">
                <span className="vht-field__label">{t('login.email')}</span>
                <input
                  className="vht-input"
                  type="email"
                  value={mockEmail}
                  onChange={(e) => setMockEmail(e.target.value)}
                />
              </label>
              <label className="vht-field">
                <span className="vht-field__label">{t('users.fieldRoles')}</span>
                <select
                  className="vht-select"
                  value={mockRole}
                  onChange={(e) => setMockRole(e.target.value)}
                >
                  {MOCK_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(`role.${r}`)}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" className="vht-btn vht-btn--primary vht-btn--lg">
                {t('login.mockLogin')}
              </button>
              <span className="login-mock__title" style={{ fontWeight: 'var(--fw-regular)' }}>
                {t('login.mockNote')}
              </span>
            </form>
          )}

          <div className="login-divider">{t('login.dividerPortal')}</div>

          {/* Het portaal — wachtwoord én authenticator-app (docs/portal.md §2). */}
          <PartnerLogin redirect={redirectParam} />

          <div className="login-help">
            <span>{t('login.helpRequest')}</span>
            <span className="t-mono">+31 33 253 41 41</span>
          </div>
        </div>
      </main>
    </div>
  );
}
